import { COUNTRIES, detectCountry } from './time.js';
/**
 * ITU-T E.164 country calling codes, keyed by ISO 3166-1 alpha-2 and stored
 * without the leading `+`.
 *
 * Kept apart from `timezones.ts` because that file is generated from the OS
 * tzdata tables and is overwritten wholesale by `scripts/gen-timezones.mjs`;
 * calling codes are not in tzdata, so they would not survive a regeneration.
 * The keys are the same 247 territories, which `PHONE_COUNTRIES` relies on.
 *
 * Territories sharing a code with their metropole share the number here too
 * (Guernsey and the Isle of Man on 44, Åland on 358). Countries with several
 * assigned NANP prefixes are listed under the oldest one — the picker only
 * ever prefills it, and any of them dials.
 */
const DIAL_CODES = {
    AD: '376', AE: '971', AF: '93', AG: '1268', AI: '1264', AL: '355', AM: '374',
    AO: '244', AQ: '672', AR: '54', AS: '1684', AT: '43', AU: '61', AW: '297',
    AX: '358', AZ: '994', BA: '387', BB: '1246', BD: '880', BE: '32', BF: '226',
    BG: '359', BH: '973', BI: '257', BJ: '229', BL: '590', BM: '1441', BN: '673',
    BO: '591', BQ: '599', BR: '55', BS: '1242', BT: '975', BW: '267', BY: '375',
    BZ: '501', CA: '1', CC: '61', CD: '243', CF: '236', CG: '242', CH: '41',
    CI: '225', CK: '682', CL: '56', CM: '237', CN: '86', CO: '57', CR: '506',
    CU: '53', CV: '238', CW: '599', CX: '61', CY: '357', CZ: '420', DE: '49',
    DJ: '253', DK: '45', DM: '1767', DO: '1809', DZ: '213', EC: '593', EE: '372',
    EG: '20', EH: '212', ER: '291', ES: '34', ET: '251', FI: '358', FJ: '679',
    FK: '500', FM: '691', FO: '298', FR: '33', GA: '241', GB: '44', GD: '1473',
    GE: '995', GF: '594', GG: '44', GH: '233', GI: '350', GL: '299', GM: '220',
    GN: '224', GP: '590', GQ: '240', GR: '30', GS: '500', GT: '502', GU: '1671',
    GW: '245', GY: '592', HK: '852', HN: '504', HR: '385', HT: '509', HU: '36',
    ID: '62', IE: '353', IL: '972', IM: '44', IN: '91', IO: '246', IQ: '964',
    IR: '98', IS: '354', IT: '39', JE: '44', JM: '1876', JO: '962', JP: '81',
    KE: '254', KG: '996', KH: '855', KI: '686', KM: '269', KN: '1869', KP: '850',
    KR: '82', KW: '965', KY: '1345', KZ: '7', LA: '856', LB: '961', LC: '1758',
    LI: '423', LK: '94', LR: '231', LS: '266', LT: '370', LU: '352', LV: '371',
    LY: '218', MA: '212', MC: '377', MD: '373', ME: '382', MF: '590', MG: '261',
    MH: '692', MK: '389', ML: '223', MM: '95', MN: '976', MO: '853', MP: '1670',
    MQ: '596', MR: '222', MS: '1664', MT: '356', MU: '230', MV: '960', MW: '265',
    MX: '52', MY: '60', MZ: '258', NA: '264', NC: '687', NE: '227', NF: '672',
    NG: '234', NI: '505', NL: '31', NO: '47', NP: '977', NR: '674', NU: '683',
    NZ: '64', OM: '968', PA: '507', PE: '51', PF: '689', PG: '675', PH: '63',
    PK: '92', PL: '48', PM: '508', PN: '64', PR: '1787', PS: '970', PT: '351',
    PW: '680', PY: '595', QA: '974', RE: '262', RO: '40', RS: '381', RU: '7',
    RW: '250', SA: '966', SB: '677', SC: '248', SD: '249', SE: '46', SG: '65',
    SH: '290', SI: '386', SJ: '47', SK: '421', SL: '232', SM: '378', SN: '221',
    SO: '252', SR: '597', SS: '211', ST: '239', SV: '503', SX: '1721', SY: '963',
    SZ: '268', TC: '1649', TD: '235', TF: '262', TG: '228', TH: '66', TJ: '992',
    TK: '690', TL: '670', TM: '993', TN: '216', TO: '676', TR: '90', TT: '1868',
    TV: '688', TW: '886', TZ: '255', UA: '380', UG: '256', UM: '1', US: '1',
    UY: '598', UZ: '998', VA: '39', VC: '1784', VE: '58', VG: '1284', VI: '1340',
    VN: '84', VU: '678', WF: '681', WS: '685', YE: '967', YT: '262', ZA: '27',
    ZM: '260', ZW: '263',
};
/**
 * Every country a number can be given for, in the same order as `COUNTRIES`
 * so the phone picker and the timezone picker read alike.
 */
export const PHONE_COUNTRIES = COUNTRIES.filter((country) => DIAL_CODES[country.code]).map((country) => ({
    code: country.code,
    name: country.name,
    dial: DIAL_CODES[country.code],
}));
const PHONE_COUNTRY_BY_CODE = new Map(PHONE_COUNTRIES.map((entry) => [entry.code, entry]));
/**
 * Where the picker starts when the device gives nothing away — a runtime with
 * no `Intl` data, or a zone like `UTC` that belongs to no country.
 */
export const FALLBACK_PHONE_COUNTRY = 'US';
/**
 * The trunk `0` is dropped when a national number is turned into E.164 almost
 * everywhere, but not here: Italian numbers keep the leading zero, and the
 * Vatican dials through the Italian plan. Stripping it there would corrupt
 * every Rome landline.
 */
const KEEPS_TRUNK_ZERO = new Set(['IT', 'VA']);
/** Longest an E.164 number can be, country code included. */
const E164_MAX_DIGITS = 15;
/**
 * Shortest a real number gets: `+290 1234` on St Helena and `+683 1234` on
 * Niue are seven digits all in, which is below anything a length rule aimed
 * at mobile numbers would allow.
 */
const E164_MIN_DIGITS = 7;
/** Stored numbers: `+` then a calling code that cannot start with zero. */
export const E164_PATTERN = /^\+[1-9]\d{6,14}$/;
export function findPhoneCountry(code) {
    return code ? PHONE_COUNTRY_BY_CODE.get(code) : undefined;
}
/** The calling code for a country, without the `+`. */
export function dialCodeOf(code) {
    return findPhoneCountry(code)?.dial;
}
/**
 * Which country's calling code to offer first, guessed from the device.
 *
 * Reuses the timezone-and-locale guess behind the country setting rather than
 * asking for GPS: a registration form should not raise a location prompt, and
 * the zone is right for anyone who is not mid-flight. It is a preselection —
 * the picker is there precisely because a guess can be wrong.
 */
export function detectPhoneCountry() {
    const detected = detectCountry();
    return detected && PHONE_COUNTRY_BY_CODE.has(detected) ? detected : FALLBACK_PHONE_COUNTRY;
}
/**
 * Combines a chosen country with whatever was typed into the number box.
 *
 * Returns `''` for an empty box so callers can treat "left blank" and "typed
 * nothing but spaces" alike. A number typed in full international form wins
 * over the picker: somebody pasting `+33 6 12 …` means that number, and
 * gluing the selected code onto the front of it would produce nonsense.
 */
export function toE164(country, national) {
    const typed = national.trim();
    if (!typed)
        return '';
    if (typed.startsWith('+') || typed.startsWith('00')) {
        const digits = typed.replace(/\D/g, '').replace(/^0+/, '');
        return digits ? `+${digits}` : '';
    }
    const dial = dialCodeOf(country) ?? dialCodeOf(FALLBACK_PHONE_COUNTRY);
    let digits = typed.replace(/\D/g, '');
    if (!KEEPS_TRUNK_ZERO.has(country))
        digits = digits.replace(/^0+/, '');
    return digits ? `+${dial}${digits}` : '';
}
/** Whether a stored value is a plausible E.164 number. */
export function isValidE164(value) {
    if (!E164_PATTERN.test(value))
        return false;
    const digits = value.length - 1;
    return digits >= E164_MIN_DIGITS && digits <= E164_MAX_DIGITS;
}
/**
 * The flag for a country, built from its ISO code as regional indicators.
 *
 * Returns the bare code on any runtime whose fonts do not draw flags, which
 * is still a usable label beside the calling code.
 */
export function countryFlag(code) {
    if (!/^[A-Za-z]{2}$/.test(code))
        return code;
    return String.fromCodePoint(...[...code.toUpperCase()].map((letter) => 0x1f1e6 + letter.charCodeAt(0) - 65));
}
